import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Button, ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link, To } from 'react-router-dom';

type IconButtonSquareProps = {
	icon: string,
	ariaLabel: string,
	onClick: () => void,
}

interface IconButtonProps
	extends Omit<ButtonProps, 'variant' | 'size'>,
	VariantProps<typeof iconButtonVariants> {
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	to?: To;
}

const iconButtonVariants = cva(
	'font-[600] transition-all duration-200 flex items-center justify-center ' +
	// Responsive sizes for the button
	'w-[48px] h-[48px] rounded-[8px] ' +
	'md:w-[56px] md:h-[56px] md:rounded-[12px] ' +
	'lg:w-[64px] lg:h-[64px] lg:rounded-[16px] ' +
	'min-[440px]:max-[639px]:w-[64px] min-[440px]:max-[639px]:h-[64px] min-[440px]:max-[639px]:rounded-[16px] ',
	{
		variants: {
			variant: {
				default:
					'text-[var(--primary-foreground)] bg-[var(--primary)] border border-[#C69F58] ' +
					'hover:text-[var(--primary-hover-foreground)] hover:bg-[var(--primary-hover)] ' +
					'focus:text-[var(--primary-hover-foreground)] focus:bg-[var(--primary-hover)] ' +
					'disabled:text-[var(--primary-disabled-foreground)] disabled:bg-[var(--primary-disabled)]',
				secondary:
					'text-[var(--secondary-foreground)] bg-[var(--secondary)] border border-[#FFFFFF05] ' +
					'hover:text-[var(--secondary-hover-foreground)] hover:bg-[var(--secondary-hover)] border hover:border-[#FFFFFF0A] ' +
					'focus:text-[var(--secondary-hover-foreground)] focus:bg-[var(--secondary-hover)] border focus:border-[#FFFFFF0A] ' +
					'disabled:text-[var(--secondary-disabled-foreground)] disabled:bg-[var(--secondary-disabled)]',
				destructive:
					'text-[var(--destructive-foreground)] bg-[var(--destructive)] ' +
					'hover:text-[var(--destructive-hover-foreground)] hover:bg-[var(--destructive-hover)] ' +
					'focus:text-[var(--destructive-hover-foreground)] focus:bg-[var(--destructive-hover)] ' +
					'disabled:text-[var(--destructive-disabled-foreground)] disabled:bg-[var(--destructive-disabled)]',
				link:
					'text-[var(--link)] bg-transparent ' +
					'hover:text-[var(--link-hover)] hover:bg-transparent ' +
					'focus:text-[var(--link-hover)] focus:bg-transparent ' +
					'disabled:text-[var(--link-disabled)] disabled:bg-transparent'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
	({ className, variant, icon: Icon, to, ...props }, ref) => {
		return (
			<Button
				asChild={!!to}
				className={cn(iconButtonVariants({ variant, className }))}
				ref={ref}
				{...props}
			>
				{to ? (
					<Link to={to} className="!p-0">
						<span className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] lg:w-[20px] lg:h-[20px] min-[440px]:max-[639px]:w-[20px] min-[440px]:max-[639px]:h-[20px]">
							<Icon className="lucide lucide-x !w-full !h-full" />
						</span>
					</Link>
				) : (
					<span className="w-[16px] h-[16px] md:w-[18px] md:h-[18px] lg:w-[20px] lg:h-[20px] min-[440px]:max-[639px]:w-[20px] min-[440px]:max-[639px]:h-[20px]">
						<Icon className="lucide lucide-x !w-full !h-full" />
					</span>
				)}
			</Button>
		);
	}
);

IconButton.displayName = 'IconButton';

export const IconButtonSquare = ({ icon, ariaLabel, onClick }: IconButtonSquareProps) => {
	return (
		<button aria-label={ariaLabel} className='IconButtonSquare' onClick={onClick} tabIndex={0}>
			<img src={icon} alt={ariaLabel} />
		</button>
	);
};
